export const scoresheetDraftId = matchId => `scoresheet-v2:${matchId}`;

export const shouldApplyScoresheetRefresh = (current, remote) =>
  Boolean(current && remote && !current.dirty && !current.conflict &&
    (Number(remote.version) > Number(current.version) ||
      remote.status !== current.status || remote.resultStatus !== current.resultStatus));

export const normalizePerformanceEntry = entry => ({
  ...entry,
  entryType: entry.entryType || (entry.metric === "Marks" ? "MarksInTurn" : "QualityPoint"),
  quantity: Math.max(1, Number(entry.quantity) || 1)
});

export const isCricketScoresheetGame = game =>
  String(game?.gameType || game?.type || "").toLowerCase() === "cricket";

export const isChicagoScoresheetGame = game =>
  String(game?.gameType || game?.type || "").toLowerCase().includes("chicago");

export const chicagoDiscipline = value => {
  const explicit = String(value?.discipline || "");
  if (["301", "501"].includes(explicit)) return explicit;
  if (explicit.toLowerCase() === "cricket") return "Cricket";
  const gameType = String(value?.gameType || value?.type || "").toLowerCase();
  if (gameType === "cricket") return "Cricket";
  const startingScore = Number(value?.startingScore ?? value?.settings?.startingScore);
  return gameType === "x01" && [301, 501].includes(startingScore) ? String(startingScore) : "";
};

export const chicagoLegSelection = (leg, index) => {
  const discipline = chicagoDiscipline(leg);
  const fixed = String(leg?.resolution || leg?.selectionMode || "").toLowerCase() === "fixed";
  return {
    legNumber: Number(leg?.legNumber) || index + 1,
    status: "NotPlayed",
    selectionMode: fixed ? "Fixed" : "Choice",
    allowedDisciplines: fixed && discipline ? [discipline] : ["301", "501", "Cricket"],
    discipline: fixed ? discipline : null,
    gameType: fixed ? (discipline === "Cricket" ? "Cricket" : "X01") : null,
    startingScore: fixed && discipline !== "Cricket" ? Number(discipline) : null,
    winnerRegistrationId: null
  };
};

export const scoresheetGameDisplayName = game => {
  if (isCricketScoresheetGame(game)) return "Cricket";
  const startingScore = Number(game?.startingScore);
  if (startingScore > 0) return String(startingScore);
  return game?.formatName || game?.name || game?.label || game?.gameType || "Game";
};

export const createScoresheetState = matchPackage => {
  const teamFor = isHome => matchPackage.teams?.find(team => Boolean(team.isHome) === isHome);
  const playersFor = (isHome, gameNumber) => {
    const team = teamFor(isHome);
    const assigned = team?.lineup?.assignments?.games?.find(value =>
      Number(value.gameNumber) === Number(gameNumber))?.playerIds || [];
    const roster = team?.roster || [];
    const players = assigned.length ? roster.filter(player => assigned.includes(player.membershipId)) : roster;
    return players.map(player => ({
      membershipId: player.membershipId,
      firstName: player.firstName,
      lastName: player.lastName
    }));
  };
  const games = (matchPackage.matchFormat?.sections || []).flatMap((section, sectionIndex) =>
    (section.games || []).map((game, gameIndex) => {
      const gameNumber = game.gameNumber || gameIndex + 1;
      const gameLabel = game.name || game.label || game.gameType || `Game ${gameNumber}`;
      const legCount = game.legCount || game.bestOf || (/chicago/i.test(`${gameLabel} ${game.gameType || ""}`) ? 3 : 1);
      const chicago = isChicagoScoresheetGame(game);
      const configuredLegs = Array.isArray(game.legs) ? game.legs : [];
      return {
        gameNumber,
        label: game.name || game.label || game.gameType || `Game ${game.gameNumber || gameIndex + 1}`,
        formatName: game.name || game.label || game.gameType || `Game ${game.gameNumber || gameIndex + 1}`,
        sectionLabel: section.name || section.label || `Section ${sectionIndex + 1}`,
        gameType: game.gameType || game.type || "Unknown",
        startingScore: Number(game.startingScore) || null,
        displayName: scoresheetGameDisplayName(game),
        status: "NotEntered",
        homePlayers: playersFor(true, gameNumber),
        awayPlayers: playersFor(false, gameNumber),
        legs: Array.from({ length: Math.max(1, Number(legCount) || 1) }, (_, index) =>
          chicago
            ? chicagoLegSelection(configuredLegs[index], index)
            : { legNumber: index + 1, winnerRegistrationId: null }),
        homeGamePoints: 0,
        awayGamePoints: 0,
        quickPoints: [],
        winnerRegistrationId: null,
        notes: "",
        lastEventId: null,
        lastUpdatedAt: null
      };
    }));
  return {
    schemaVersion: 3,
    games: games.length ? games : [{
      gameNumber: 1,
      label: "Game 1",
      sectionLabel: "Match",
      gameType: "Unknown",
      status: "NotEntered",
      homePlayers: [],
      awayPlayers: [],
      legs: [{ legNumber: 1, winnerRegistrationId: null }],
      homeGamePoints: 0,
      awayGamePoints: 0,
      quickPoints: [],
      winnerRegistrationId: null,
      notes: "",
      lastEventId: null,
      lastUpdatedAt: null
    }]
  };
};

export const normalizeScoresheetState = (state, matchPackage) => {
  const template = createScoresheetState(matchPackage);
  if (!state?.games?.length) return template;
  return {
    ...state,
    games: template.games.map(templateGame => {
      const saved = state.games.find(game => Number(game.gameNumber) === Number(templateGame.gameNumber));
      return saved ? {
        ...templateGame,
        ...saved,
        homePlayers: saved.homePlayers?.length ? saved.homePlayers : templateGame.homePlayers,
        awayPlayers: saved.awayPlayers?.length ? saved.awayPlayers : templateGame.awayPlayers,
        legs: saved.legs?.length ? saved.legs : templateGame.legs,
        quickPoints: Array.isArray(saved.quickPoints)
          ? saved.quickPoints.map(normalizePerformanceEntry)
          : []
      } : templateGame;
    })
  };
};

export const hydrateScoresheet = (remote, draft, matchPackage) => {
  const template = createScoresheetState(matchPackage);
  const remoteState = normalizeScoresheetState(remote?.state, matchPackage);
  if (draft?.dirty) {
    return {
      ...remote,
      state: normalizeScoresheetState(draft.state, matchPackage),
      currentGameNumber: draft.currentGameNumber || remote?.currentGameNumber || template.games[0].gameNumber,
      pendingEvents: draft.pendingEvents || [],
      dirty: true,
      conflict: Boolean(remote && draft.baseVersion !== remote.version)
    };
  }
  return {
    ...remote,
    state: remoteState,
    currentGameNumber: remote?.currentGameNumber || draft?.currentGameNumber || remoteState.games[0].gameNumber,
    pendingEvents: [],
    dirty: false,
    conflict: false
  };
};

export const matchScoresheetTotals = (state, homeRegistrationId, awayRegistrationId) =>
  (state?.games || []).reduce((totals, game) => ({
    homeWins: totals.homeWins + (game.winnerRegistrationId === homeRegistrationId ? 1 : 0),
    awayWins: totals.awayWins + (game.winnerRegistrationId === awayRegistrationId ? 1 : 0),
    homeGamePoints: totals.homeGamePoints + (Number(game.homeGamePoints) || 0),
    awayGamePoints: totals.awayGamePoints + (Number(game.awayGamePoints) || 0),
    homeQuickPoints: totals.homeQuickPoints + (game.quickPoints || [])
      .map(normalizePerformanceEntry)
      .filter(value => value.side === "home" && value.entryType === "QualityPoint")
      .reduce((count, value) => count + value.quantity, 0),
    awayQuickPoints: totals.awayQuickPoints + (game.quickPoints || [])
      .map(normalizePerformanceEntry)
      .filter(value => value.side === "away" && value.entryType === "QualityPoint")
      .reduce((count, value) => count + value.quantity, 0)
  }), { homeWins: 0, awayWins: 0, homeGamePoints: 0, awayGamePoints: 0, homeQuickPoints: 0, awayQuickPoints: 0 });

export const applyScoresheetGameChange = ({
  state,
  updatedGame,
  eventName,
  payload,
  eventId,
  correctsEventId = null,
  occurredAt
}) => {
  const savedGame = { ...updatedGame, lastEventId: eventId, lastUpdatedAt: occurredAt };
  return {
    state: {
      ...state,
      games: state.games.map(game =>
        Number(game.gameNumber) === Number(savedGame.gameNumber) ? savedGame : game)
    },
    event: {
      id: eventId,
      gameNumber: savedGame.gameNumber,
      eventType: `${eventName}${correctsEventId ? "Corrected" : "Recorded"}`,
      payload: { gameNumber: savedGame.gameNumber, ...payload },
      correctsEventId,
      occurredAt
    }
  };
};

export const reviewScoresheet = (state, homeRegistrationId, awayRegistrationId) => {
  const games = state?.games || [];
  const errors = [];
  for (const game of games) {
    if (game.status !== "Complete")
      errors.push({ gameNumber: game.gameNumber, field: "Status", message: "Select the game winner." });
    if (![homeRegistrationId, awayRegistrationId].includes(game.winnerRegistrationId))
      errors.push({ gameNumber: game.gameNumber, field: "Winner", message: "Choose Home or Away as the winner." });
    if (isChicagoScoresheetGame(game) && game.status === "Complete") {
      const played = (game.legs || []).filter(leg => [homeRegistrationId, awayRegistrationId].includes(leg.winnerRegistrationId));
      const disciplines = played.map(chicagoDiscipline).filter(Boolean);
      const homeWins = played.filter(leg => leg.winnerRegistrationId === homeRegistrationId).length;
      const awayWins = played.filter(leg => leg.winnerRegistrationId === awayRegistrationId).length;
      const expectedWinner = homeWins >= 2 ? homeRegistrationId : awayWins >= 2 ? awayRegistrationId : null;
      if (played.length < 2 || played.length > 3 || disciplines.length !== played.length)
        errors.push({ gameNumber: game.gameNumber, field: "Legs", message: "Record 301, 501, or Cricket and a winner for each played Chicago leg." });
      if (new Set(disciplines).size !== disciplines.length)
        errors.push({ gameNumber: game.gameNumber, field: "Legs", message: "A Chicago discipline cannot be played twice in the same set." });
      if (!expectedWinner || expectedWinner !== game.winnerRegistrationId)
        errors.push({ gameNumber: game.gameNumber, field: "Winner", message: "The Chicago winner must agree with the leg winners." });
      if (played.length === 3 && played.slice(0, 2).every(leg => leg.winnerRegistrationId === played[0].winnerRegistrationId))
        errors.push({ gameNumber: game.gameNumber, field: "Legs", message: "Leg 3 cannot be played after a 2–0 result." });
    }
    for (const quickPoint of (game.quickPoints || []).map(normalizePerformanceEntry)) {
      const validPlayers = quickPoint.side === "home" ? game.homePlayers || [] : quickPoint.side === "away" ? game.awayPlayers || [] : [];
      if (!validPlayers.some(player => player.membershipId === quickPoint.playerMembershipId) ||
          Number(quickPoint.value) <= 0 || Number(quickPoint.quantity) <= 0)
        errors.push({ gameNumber: game.gameNumber, field: "PerformanceEntries", message: "Correct the player, value, or quantity on this performance entry." });
    }
  }
  if (!games.length) errors.push({ gameNumber: null, field: "Games", message: "No games are available to submit." });
  return {
    gameCount: games.length,
    completedGames: games.filter(game => game.status === "Complete").length,
    errors,
    canSubmit: games.length > 0 && errors.length === 0
  };
};
